import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import SEOHead from '../seo/SEOHead';
import { BreadcrumbSchema, LocalBusinessSchema } from '../seo/SchemaMarkup';
import { PAGE_SEO, SITE_URL, COMPANY_INFO } from '../../seoConfig';

const KontaktPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const breadcrumbs = [
    { name: "Start", url: SITE_URL },
    { name: "Kontakt", url: `${SITE_URL}/kontakt` }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would send the form data to your backend
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <SEOHead 
        title={PAGE_SEO.kontakt.title}
        description={PAGE_SEO.kontakt.description}
        canonical={PAGE_SEO.kontakt.canonical}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <LocalBusinessSchema />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-24">
          <div className="container mx-auto px-6 relative z-10">
            {/* Breadcrumb */}
            <nav className="text-sm mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                <li><Link to="/" className="text-gray-400 hover:text-white">Start</Link></li>
                <li className="text-gray-500">/</li>
                <li className="text-[#DC2626]">Kontakt</li>
              </ol>
            </nav>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-[#DC2626]">Kontakt</span> aufnehmen
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              Haben Sie Fragen oder möchten Sie ein unverbindliches Angebot? Wir freuen uns auf Ihre Nachricht.
            </p>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Schreiben Sie uns</h2>
                
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Vielen Dank!</h3>
                    <p className="text-gray-600">
                      Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns schnellstmöglich bei Ihnen.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
                        placeholder="Ihr Name"
                        data-testid="contact-name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
                        placeholder="ihre@email.de"
                        data-testid="contact-email"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
                        placeholder="Ihre Telefonnummer"
                        data-testid="contact-phone"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                        Nachricht *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC2626] focus:border-transparent resize-none"
                        placeholder="Beschreiben Sie Ihr Anliegen..."
                        data-testid="contact-message"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 bg-[#DC2626] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#B91C1C] transition-colors"
                      data-testid="contact-submit"
                    >
                      <Send className="w-5 h-5" />
                      Nachricht senden
                    </button>
                    
                    <p className="text-sm text-gray-500 text-center">
                      Mit dem Absenden stimmen Sie unserer <Link to="/datenschutz" className="text-[#DC2626] hover:underline">Datenschutzerklärung</Link> zu.
                    </p>
                  </form>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Kontaktdaten</h2>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-[#DC2626]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Telefon</h3>
                        <a href="tel:+491729308274" className="text-gray-600 hover:text-[#DC2626] transition-colors block">
                          0172 9308274 (Mobil)
                        </a>
                        <a href="tel:+4971635357890" className="text-gray-600 hover:text-[#DC2626] transition-colors block">
                          07163 5357890 (Festnetz)
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#DC2626]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">E-Mail</h3>
                        <a href={`mailto:${COMPANY_INFO.email}`} className="text-gray-600 hover:text-[#DC2626] transition-colors">
                          {COMPANY_INFO.email}
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#DC2626]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Adresse</h3>
                        <p className="text-gray-600">
                          {COMPANY_INFO.address.streetAddress}<br />
                          {COMPANY_INFO.address.postalCode} {COMPANY_INFO.address.addressLocality}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-[#DC2626]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Erreichbarkeit</h3>
                        <p className="text-gray-600">
                          Mo - Fr: 7:00 - 18:00 Uhr<br />
                          Sa: nach Vereinbarung
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Service Area */}
                <div className="bg-[#DC2626] rounded-2xl p-8 text-white">
                  <h2 className="text-2xl font-bold mb-4">Unser Einsatzgebiet</h2>
                  <p className="text-white/90 mb-4">
                    Wir sind in Stuttgart und der gesamten Region Stuttgart tätig. Für größere Projekte auch überregional in Baden-Württemberg.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Stuttgart und Umgebung (ca. 50 km)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Esslingen, Ludwigsburg, Böblingen
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Überregional auf Anfrage
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default KontaktPage;
